import { Suspense, lazy, useEffect, useState, type ComponentType } from 'react';
import { Link, Outlet, useNavigate } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Bell,
  ChartColumn,
  CreditCard,
  House,
  Inbox,
  LockKeyhole,
  Settings,
  ShieldCheck,
  Smartphone,
  Users,
  type LucideProps,
} from 'lucide-react';
import { unwrap } from '@kidova/api-client';
import AccountMenu from './AccountMenu';
import MoreSheet from './MoreSheet';
import { useI18n, type MessageKey } from '../i18n';
import { api, rateGate } from '../lib/api';
import { authHint } from '../lib/preferences';
import { clearCache } from '../lib/query-client';
import { q } from '../lib/queries';
import { useRateLimited } from '../lib/rate-limit';

type To =
  | '/'
  | '/children'
  | '/devices'
  | '/requests'
  | '/reports'
  | '/vpn'
  | '/billing'
  | '/privacy'
  | '/settings';

interface NavItem {
  to: To;
  label: MessageKey;
  icon: ComponentType<LucideProps>;
}

const NAV: NavItem[] = [
  { to: '/', label: 'nav.home', icon: House },
  { to: '/children', label: 'nav.children', icon: Users },
  { to: '/devices', label: 'nav.devices', icon: Smartphone },
  { to: '/requests', label: 'nav.requests', icon: Inbox },
  { to: '/reports', label: 'nav.reports', icon: ChartColumn },
  { to: '/vpn', label: 'nav.vpn', icon: ShieldCheck },
  { to: '/billing', label: 'nav.billing', icon: CreditCard },
  { to: '/privacy', label: 'nav.privacy', icon: LockKeyhole },
  { to: '/settings', label: 'nav.settings', icon: Settings },
];
// Below lg the first four go in the bottom tab bar; the rest sit behind "More" (§23.4).
const TABS = NAV.slice(0, 4);
export const MORE = NAV.slice(4);

// Only families in the §19.7 grace period see it; it arrives with the billing data it depends on.
const GraceBanner = lazy(() => import('./GraceBanner'));
const RateLimitNotice = lazy(() => import('./RateLimitNotice'));

export function useSignOut() {
  const client = useQueryClient();
  const navigate = useNavigate();
  return async () => {
    try {
      await unwrap(api.DELETE('/v1/web/session'));
    } finally {
      authHint.set(false);
      clearCache(client);
      await navigate({ to: '/login', search: { next: undefined } });
    }
  };
}

function Brand({ compact }: { compact?: boolean }) {
  return (
    <Link
      to="/"
      className="flex items-center gap-2.5 rounded-button px-2 py-1 text-text-primary no-underline"
    >
      <img src="/brand/mark.svg" alt="" width={32} height={32} className="dark:hidden" />
      <img src="/brand/mark-dark.svg" alt="" width={32} height={32} className="hidden dark:block" />
      <span className={`text-h3 font-semibold tracking-tight ${compact ? 'xl:inline hidden' : ''}`}>
        Kidova
      </span>
    </Link>
  );
}

export function AppShell() {
  const { t, number } = useI18n();
  const session = useQuery(q.session());
  const home = useQuery(q.home());
  const billing = useQuery({ ...q.billing(), staleTime: 60_000 });
  const [moreOpen, setMoreOpen] = useState(false);
  const client = useQueryClient();
  const limited = useRateLimited(rateGate);
  const signedIn = !!session.data;
  useEffect(() => {
    if (!signedIn) return;
    // The stream's code loads after the first screen, like menus and dialogs (ADR 0602).
    let stop: (() => void) | undefined;
    let closed = false;
    void import('../lib/live').then((live) => {
      if (!closed) stop = live.startLiveUpdates(client);
    });
    return () => {
      closed = true;
      stop?.();
    };
  }, [client, signedIn]);

  const pending = home.data?.pending_requests ?? 0;
  const parent = session.data?.parent;
  const name = parent?.display_name ?? 'Kidova';
  const contract = billing.data?.contract;

  return (
    <div className="app-shell">
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:fixed focus:start-4 focus:top-4 focus:z-50 focus:rounded-button focus:bg-bg-surface focus:px-4 focus:py-2"
      >
        {t('nav.skip')}
      </a>
      <div className="hidden border-e border-border-subtle/70 bg-bg-surface lg:block">
        <aside className="sticky top-0 flex h-dvh flex-col gap-2 px-3 py-4">
          <div className="mb-4 flex justify-center xl:justify-start">
            <Brand compact />
          </div>
          <nav aria-label={t('nav.main')} className="flex flex-1 flex-col gap-1">
            {NAV.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                activeOptions={{ exact: item.to === '/' }}
                className="app-nav-link justify-center xl:justify-start"
                aria-label={t(item.label)}
                title={t(item.label)}
              >
                <item.icon size={20} strokeWidth={1.75} aria-hidden />
                <span className="hidden xl:inline">{t(item.label)}</span>
                {item.to === '/requests' && pending > 0 && (
                  <span className="ms-auto hidden min-w-6 rounded-full bg-fill-accent px-1.5 text-center text-caption font-semibold text-text-on-accent xl:inline num">
                    {number(pending)}
                  </span>
                )}
              </Link>
            ))}
          </nav>
          <div className="hidden flex-col gap-1 border-t border-border-subtle/70 pt-3 text-small xl:flex">
            <Link to="/cancel" className="px-3 py-1 text-text-secondary hover:text-text-primary">
              {t('nav.cancelContracts')}
            </Link>
            <a
              href="https://status.kidova.com"
              className="px-3 py-1 text-text-secondary hover:text-text-primary"
            >
              {t('nav.status')}
            </a>
          </div>
        </aside>
      </div>

      <div className="flex min-w-0 flex-col">
        <header className="sticky top-0 z-10 border-b border-border-subtle/70 bg-bg-page pt-[env(safe-area-inset-top)]">
          <div className="mx-auto flex h-16 w-full max-w-[1280px] items-center gap-3 px-4 md:px-8">
            <div className="lg:hidden">
              <Brand />
            </div>
            <p className="hidden truncate text-body font-medium text-text-secondary lg:block">
              {session.data?.family.name}
            </p>
            <div className="ms-auto flex items-center gap-1">
              <Link
                to="/requests"
                search={{ tab: 'pending' }}
                className="relative inline-flex size-10 items-center justify-center rounded-full text-text-secondary hover:bg-[var(--kd-hover-wash)]"
                aria-label={t('shell.requestsBadge', { count: pending })}
                data-testid="requests-bell"
              >
                <Bell size={20} strokeWidth={1.75} aria-hidden />
                {pending > 0 && (
                  <span className="absolute end-1 top-1 min-w-5 rounded-full bg-fill-accent px-1 text-center text-caption font-semibold leading-5 text-text-on-accent num">
                    {number(pending)}
                  </span>
                )}
              </Link>
              <AccountMenu name={name} />
            </div>
          </div>
        </header>

        <main
          id="main"
          tabIndex={-1}
          className="mx-auto w-full max-w-[1280px] flex-1 px-4 pb-28 pt-6 outline-none md:px-8 lg:pb-12 lg:pt-8"
        >
          {contract?.grace_until && (
            <Suspense fallback={null}>
              <GraceBanner
                until={contract.grace_until}
                plan={t(`billing.plan.${billing.data?.plan ?? 'family'}`)}
              />
            </Suspense>
          )}
          {limited && (
            <Suspense fallback={null}>
              <RateLimitNotice />
            </Suspense>
          )}
          <Outlet />
        </main>
      </div>

      <nav
        aria-label={t('nav.main')}
        className="fixed inset-x-0 bottom-0 z-10 flex border-t border-border-subtle/70 bg-bg-surface pb-[env(safe-area-inset-bottom)] lg:hidden"
      >
        {TABS.map((item) => (
          <Link
            key={item.to}
            to={item.to}
            activeOptions={{ exact: item.to === '/' }}
            className="app-tab-link"
          >
            <item.icon size={22} strokeWidth={1.75} aria-hidden />
            <span>{t(item.label)}</span>
          </Link>
        ))}
        <MoreSheet open={moreOpen} onOpenChange={setMoreOpen} />
      </nav>
    </div>
  );
}
